import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronDown, ChevronsLeft, ChevronsRight, ChevronsUpDown, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Brand, UserAvatar } from './Brand';
import { ADMIN_NAV, displayName, NavItem, PRIMARY_NAV, roleLabel, visibleTo } from './navigation';

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

const ADMIN_OPEN_KEY = 'staketory.sidebar.adminOpen';

/** Desktop navigation. On smaller screens the tab bar takes over. */
export const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onToggleCollapsed }) => {
  const { user } = useAuth();
  const [adminOpen, setAdminOpen] = useState(() => {
    try {
      return localStorage.getItem(ADMIN_OPEN_KEY) !== 'false';
    } catch {
      return true;
    }
  });

  const primary = visibleTo(PRIMARY_NAV, user?.role);
  const admin = visibleTo(ADMIN_NAV, user?.role);

  const toggleAdmin = () => {
    setAdminOpen((open) => {
      try {
        localStorage.setItem(ADMIN_OPEN_KEY, String(!open));
      } catch {}
      return !open;
    });
  };

  return (
    <aside
      className={`sticky top-0 hidden h-screen flex-shrink-0 flex-col border-r border-neutral-200/70 bg-neutral-100/60 font-ui transition-[width] duration-200 ease-out lg:flex ${
        collapsed ? 'w-[72px]' : 'w-[248px]'
      }`}
      aria-label="Main"
    >
      {/* Brand */}
      <div className={`flex h-16 flex-shrink-0 items-center ${collapsed ? 'justify-center' : 'justify-between pl-5 pr-3'}`}>
        <NavLink to="/dashboard" className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40">
          <Brand markOnly={collapsed} />
        </NavLink>
        {!collapsed && (
          <CollapseButton collapsed={collapsed} onClick={onToggleCollapsed} />
        )}
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 pb-4 [scrollbar-width:none]">
        {collapsed && (
          <div className="mb-2 flex justify-center">
            <CollapseButton collapsed={collapsed} onClick={onToggleCollapsed} />
          </div>
        )}

        <ul className="space-y-0.5">
          {primary.map((item) => (
            <li key={item.href}>
              <SidebarLink item={item} collapsed={collapsed} />
            </li>
          ))}
        </ul>

        {admin.length > 0 && (
          <div className="mt-6">
            {collapsed ? (
              <div className="mx-auto mb-2 h-px w-6 bg-neutral-200" aria-hidden="true" />
            ) : (
              <button
                type="button"
                onClick={toggleAdmin}
                aria-expanded={adminOpen}
                className="mb-1 flex h-8 w-full items-center gap-1.5 rounded-lg px-3 text-[12px] text-neutral-400 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
              >
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-200 ${adminOpen ? '' : '-rotate-90'}`}
                  strokeWidth={1.75}
                />
                Admin
              </button>
            )}
            {(collapsed || adminOpen) && (
              <ul className="space-y-0.5">
                {admin.map((item) => (
                  <li key={item.href}>
                    <SidebarLink item={item} collapsed={collapsed} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </nav>

      <div className="flex-shrink-0 p-3">
        <AccountMenu collapsed={collapsed} />
      </div>
    </aside>
  );
};

const CollapseButton: React.FC<{ collapsed: boolean; onClick?: () => void }> = ({ collapsed, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
    title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white text-neutral-500 ring-1 ring-neutral-200/80 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
  >
    {collapsed ? <ChevronsRight className="h-3.5 w-3.5" strokeWidth={1.75} /> : <ChevronsLeft className="h-3.5 w-3.5" strokeWidth={1.75} />}
  </button>
);

const SidebarLink: React.FC<{ item: NavItem; collapsed: boolean }> = ({ item, collapsed }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.href}
      title={collapsed ? item.name : undefined}
      aria-label={collapsed ? item.name : undefined}
      className={({ isActive }) =>
        `group flex h-9 items-center rounded-xl text-[14px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
          collapsed ? 'mx-auto w-10 justify-center' : 'gap-3 px-3'
        } ${
          isActive
            ? 'bg-white text-ink shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-neutral-200/80'
            : 'text-neutral-500 hover:bg-neutral-200/50 hover:text-ink'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={`h-[18px] w-[18px] flex-shrink-0 transition-colors ${
              isActive ? 'text-primary-600' : 'text-neutral-400 group-hover:text-ink'
            }`}
            strokeWidth={1.75}
            aria-hidden="true"
          />
          {!collapsed && <span className="truncate">{item.name}</span>}
        </>
      )}
    </NavLink>
  );
};

const AccountMenu: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={collapsed ? `Account: ${displayName(user)}` : undefined}
        className={`flex w-full items-center rounded-2xl text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
          collapsed ? 'justify-center p-1' : 'gap-3 bg-white p-2 pr-3 ring-1 ring-neutral-200/80 hover:bg-neutral-50'
        }`}
      >
        <UserAvatar user={user} />
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] text-ink">{displayName(user)}</span>
              <span className="block truncate text-[12px] text-neutral-400">{roleLabel(user?.role)}</span>
            </span>
            <ChevronsUpDown className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
          </>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute z-50 w-56 animate-fade-up rounded-2xl bg-white p-1.5 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.2)] ring-1 ring-black/[0.06] ${
            collapsed ? 'bottom-0 left-full ml-3' : 'bottom-full left-0 mb-2'
          }`}
        >
          {user?.email && <p className="truncate px-3 pb-1.5 pt-2 text-xs text-neutral-400">{user.email}</p>}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              logout();
            }}
            className="flex h-9 w-full items-center gap-2.5 rounded-xl px-3 text-[13px] text-ink transition-colors hover:bg-neutral-100 focus-visible:bg-neutral-100 focus-visible:outline-none"
          >
            <LogOut className="h-4 w-4 text-neutral-400" strokeWidth={1.75} />
            Log out
          </button>
        </div>
      )}
    </div>
  );
};
